import {TriangleDownIcon, type Icon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button} from '@primer/react'
import {memo, useCallback, useMemo} from 'react'
import {
  DockedPanel,
  useEnabledDockedPanels,
  useActiveDockedPanel,
  useDockedPanelActions,
} from '../../contexts/DockedPanelContext'
import styles from './Panel.module.css'
import {getPanelHeading, getPanelIcon, getPanelOpenMetadata, usePanelSwitcherSelectCallback} from './panel-registry'

export function PanelNavigationDropdown() {
  const activePanel = useActiveDockedPanel()
  const enabledPanels = useEnabledDockedPanels()

  const currentValue = useMemo(() => {
    return activePanel || DockedPanel.Comments
  }, [activePanel])

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Button
          aria-label={getPanelHeading(currentValue)}
          leadingVisual={getPanelIcon(currentValue)}
          trailingVisual={TriangleDownIcon}
          size="small"
          variant="invisible"
          className={styles.PanelNavigationDropdown}
        >
          {getPanelHeading(currentValue)}
        </Button>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay align="start">
        <ActionList selectionVariant="single" showDividers={false}>
          {enabledPanels.map(value => (
            <PanelNavigationItem key={value} value={value} selected={value === currentValue} />
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

interface PanelNavigationItemProps {
  value: DockedPanel
  selected: boolean
}

const PanelNavigationItem = memo(function PanelNavigationItemComponent({value, selected}: PanelNavigationItemProps) {
  const {openPanel} = useDockedPanelActions()
  const icon = getPanelIcon(value)
  const onPanelSwitcherSelect = usePanelSwitcherSelectCallback(value)

  const handleSelect = useCallback(() => {
    const metadata = getPanelOpenMetadata(value)
    openPanel(value, metadata ? {metadata} : undefined)
    onPanelSwitcherSelect?.()
  }, [onPanelSwitcherSelect, openPanel, value])

  return <PanelNavigationActionListItem value={value} selected={selected} icon={icon} onSelect={handleSelect} />
})

interface PanelNavigationActionListItemProps extends PanelNavigationItemProps {
  icon: Icon
  onSelect: () => void
}

function PanelNavigationActionListItem({
  value,
  selected,
  icon: LeadingVisual,
  onSelect,
}: PanelNavigationActionListItemProps) {
  return (
    <ActionList.Item selected={selected} onSelect={onSelect}>
      <ActionList.LeadingVisual>
        <LeadingVisual />
      </ActionList.LeadingVisual>
      {getPanelHeading(value)}
    </ActionList.Item>
  )
}
